import React, { useEffect, useState } from 'react'
import useNavigation from '../../../hooks/use-navigation'
import { IntlFormatters } from 'react-intl'
import { observer } from 'mobx-react-lite'
import { Box, Flex, Text } from '../../../vanilla'
import type { BasketStore, ProductItem } from '../../../store/BasketStore'
import type { AutocompleteComponents } from '@algolia/autocomplete-js'
import * as styles from './styles.css'
import { ATBButton } from '../../add-to-basket-button'
import { CART_LOCATION } from '../../../analytics/utils'
import { EVENT_NAMES_MAP } from '../../../analytics/algolia'
import { Hit, ProductModel } from '../../../store/ProductStore'
import { ModalNavigationProps } from '../../../types/ui'
import { NearPromo } from '../../product-detail/product-promo'
import { Price } from '../../price'
import { createRestrictedMessage } from '../util/utils'
import { formatProductName } from '../../../utils/utils'
import { isMobile } from '../../../utils/utils'
import { transformHit } from '../util/transformHit'
import { useAddToCartTracking } from '../../../analytics/click-event-tracking'
import { useAlgoliaAnalytics, useBookDeliveryPopover } from '../../../contexts'
import { useBasketItem } from '../../../store/hooks/useBasketItem'
import { useCustomerStore, useGlobalStore, useOrderStore } from '../../../store/hooks/useStore'
import { useItemPrice } from '../util/useItemPrice'
import { useProductImage } from '../../../store/hooks/useProductImage'
import { useSlotExpiration } from '../../../store/hooks/useSlotExpiration'
import { vars } from '../../../vanilla/vars.css'
import { PricePerUnit } from '../../price-per-unit'
import UpdateCartItemQuantity from '../../update-cart-quantity'
import useEinstein from '../../../store/hooks/useEinstein'

interface AutocompleteHitProps {
  hit: Hit
  components: AutocompleteComponents
  formatNumber: IntlFormatters['formatNumber']
  basketStore: BasketStore
  // `navigate` is required to be passed as Hit Item is rendered out of react-router context
  navigate: ReturnType<typeof useNavigation>
  searchQuery: string
  modalNavigationProps: ModalNavigationProps
  hitItemIndex?: number
}

// Note: we are not allowed to use Link as we don't have Router context in this template,
// instead we are using navigate passed from parent.
export const HitComponent = observer(
  ({hit, components, basketStore, navigate, searchQuery, modalNavigationProps, hitItemIndex}: AutocompleteHitProps) => {
    const {events} = useAlgoliaAnalytics()
    const {sendClickSearch} = useEinstein()
    const img = hit.image_groups?.[0]?.images?.[0]
    const imgSrc = useProductImage(img)
    const {displayPrice, promotionPrice} = useItemPrice(hit as unknown as ProductModel)
    const {quantity} = useBasketItem(hit.id)
    const [clickedQuantity, setClickedQuantity] = useState(0)
    const {showBookDeliveryPopover} = useBookDeliveryPopover()
    const customerStore = useCustomerStore()
    const {isReservationExpired, handleEditReservationExpired} = useSlotExpiration()
    const {editMode} = useOrderStore()
    const isMobileFlag = isMobile()
    const {getApplicablePromotions} = useCustomerStore()
    const {nearPromo, bundle} = getApplicablePromotions(hit.productPromotions)
    const {selectedStoreId, selectedPriceDayOffset, selectedRegion} = useGlobalStore()

    // Restricted product classes
    const restrictedProductInfo = basketStore.getRestrictedProductInfo(hit.productClasses, hit.id)

    useEffect(() => {
      setClickedQuantity(quantity)
    }, [basketStore.count, hit, quantity])

    const addToBasket = (e: React.MouseEvent<HTMLButtonElement>) => {
      e.preventDefault()
      e.stopPropagation()
      customerStore.setLastActiveToNow()

      if (editMode && isReservationExpired()) {
        // If in edit mode and slot has expired send to order history and don't add to basket
        handleEditReservationExpired()
      } else if (!basketStore.canAddItems) {
        showBookDeliveryPopover({
          placement: window.innerWidth < 768 ? 'bottom' : 'center',
          bookDeliveryProps: {
            search: true,
            product: hit as ProductItem,
            onNavigate: navigate,
          },
        })
      } else {
        basketStore.addProductToUpdate({
          product: hit as ProductItem,
          quantity: 1,
          event: EVENT_NAMES_MAP.AUTOCOMPLETE_ADD_TO_CART,
          isSearch: true,
        })
        setClickedQuantity(1)
      }
    }

    const productLink = `/p/${formatProductName(hit?.name)}/${hit.id}.html`
    const sendAddToCartData = useAddToCartTracking(basketStore.basket)

    const sendItemClickEvents = () => {
      /* Einstein Analytics Events */
      sendClickSearch({
        searchText: searchQuery,
        product: {
          id: hit.id,
        },
      })

      /* Algolia Analytics Events */
      events?.autocomplete?.sendItemClickEvent({
        objectIDs: [hit.objectID],
        queryID: hit.__queryID || hit.__autocomplete_queryID,
        positions: [(hit.__autocomplete_id as number) + 1],
      })
    }

    if (isMobileFlag) {
      return (
        <Flex direction="row" gap="8px" className="autocomplete-hit-price-offer">
          <Flex direction="column" gap="8px" width="full">
            <Flex align="center" gap="12px">
              <Box
                flexShrink="0"
                width="48px"
                height="48px"
                onClick={() => {
                  sendItemClickEvents()
                  navigate(productLink)
                }}
              >
                <img
                  src={`${imgSrc}?w=128&qlt=60`}
                  alt={img?.alt}
                  style={{display: 'block', width: '100%', height: 'auto', objectFit: 'contain'}}
                />
              </Box>
              <Flex direction='column' alignSelf="flex-start" gap="2px">
                <Text
                  height="full"
                  variant="text2"
                  lineHeight="normal"
                  flex="1"
                  color="text1"
                  className={styles.itemText}
                  onClick={() => {
                    sendItemClickEvents()
                    navigate(productLink)
                  }}
                >
                  <components.Highlight hit={hit} attribute="name" />
                </Text>
                {restrictedProductInfo?.cannotAddToBasket && hit.productClasses?.length > 0 && (
                  <Text color='accent0'>{createRestrictedMessage(restrictedProductInfo?.restrictedProductLimit)}</Text>
                )}
                <PricePerUnit 
                  price={displayPrice}
                  promotionPrice={promotionPrice}
                  priceInfo={hit.priceInfo}
                />
              </Flex>
            </Flex>
            {(nearPromo || bundle) && isMobileFlag && (
            <NearPromo
              data-test-selector="product-search-suggestion-promo-badge"
              position="static"
              promotion={nearPromo}
              bundle={bundle}
              onNavigate={navigate}
              search={true}
              hitId={hit.id}
            />
          )}
          </Flex>
          <Flex direction="column" gap="8px" alignItems="flex-end">
            <Price price={displayPrice} promotionPrice={promotionPrice} priceInfo={hit.priceInfo} isSearch={true} />
            <Box style={{width: '144px'}}>
              <UpdateCartItemQuantity
                product={hit as ProductItem}
                maxQuantity={restrictedProductInfo?.maxQuantityAllowed}
                quantity={clickedQuantity}
                setQuantity={setClickedQuantity}
                cartLocation={CART_LOCATION.AUTOCOMPLETE}
                searchQuery={searchQuery}
                hitItemIndex={hitItemIndex}
                renderButton={
                  !restrictedProductInfo?.cannotAddToBasket ? (
                  <ATBButton
                    hit={transformHit(hit, selectedPriceDayOffset, selectedStoreId, selectedRegion) as Hit}
                    displayPrice={displayPrice}
                    navigate={navigate}
                    ATBEventDetails={{
                      locationOfCart: CART_LOCATION.AUTOCOMPLETE,
                      viewingCategoryId: '',
                      searchQuery: searchQuery
                    }}
                    onClick={(e) => {
                      addToBasket(e)
                      sendAddToCartData(
                        hit,
                        CART_LOCATION.AUTOCOMPLETE,
                        '', // No viewingCategoryId for autocomplete hits
                        searchQuery,
                        hitItemIndex
                      )
                    }}
                    width="full"
                    isSearch={true}
                  />
                  ) : undefined
                }
                style={{width: '144px', height: vars.space['32px']}}
              />
            </Box>
          </Flex>
        </Flex>
      )
    }

    return (
      <Flex gap="12px" align="flex-start">
        <Box
          flexShrink="0"
          width="48px"
          height="48px"
          onClick={() => {
            sendItemClickEvents()
            navigate(productLink)
          }}
        >
          <img
            src={`${imgSrc}?w=128&qlt=60`}
            alt={img?.alt}
            style={{display: 'block', width: '100%', height: 'auto', objectFit: 'contain'}}
          />
        </Box>

        <Flex flex="1" minWidth="0px" direction="column" gap="8px" alignSelf="stretch" className="autocomplete-hit-price-offer">
          <Flex direction='column'>
            <Text
              height="full"
              variant="text2"
              lineHeight="shorter"
              flex="1"
              color="text1"
              className={styles.itemText}
              onClick={() => {
                sendItemClickEvents()
                navigate(productLink)
              }}
            >
              <components.Highlight hit={transformHit(hit, selectedPriceDayOffset, selectedStoreId, selectedRegion) as Hit} attribute="name" />
            </Text>
            {restrictedProductInfo?.cannotAddToBasket && hit.productClasses?.length > 0 && (
              <Text color='accent0'>{createRestrictedMessage(restrictedProductInfo?.restrictedProductLimit)}</Text>
            )}
            <PricePerUnit 
              price={displayPrice}
              promotionPrice={promotionPrice}
              priceInfo={hit.priceInfo}
            />
          </Flex>
          {nearPromo && !isMobileFlag && (
            <NearPromo
              data-test-selector="product-search-suggestion-promo-badge"
              position="static"
              promotion={nearPromo}
              onNavigate={navigate}
              search={true}
              modalNavigationProps={modalNavigationProps}
              hitId={hit.id}
            />
          )}
        </Flex>
        <Flex direction={["column", "row"]} gap={["8px", "12px"]} alignItems="flex-start">
          <Price price={displayPrice} promotionPrice={promotionPrice} priceInfo={hit.priceInfo} isSearch={true} />
          <Box width="full">
            <UpdateCartItemQuantity
              product={hit as ProductItem}
              maxQuantity={restrictedProductInfo?.maxQuantityAllowed}
              quantity={clickedQuantity}
              setQuantity={setClickedQuantity}
              cartLocation={CART_LOCATION.AUTOCOMPLETE}
              searchQuery={searchQuery}
              hitItemIndex={hitItemIndex}
              renderButton={
                !restrictedProductInfo?.cannotAddToBasket ? (
                <ATBButton
                  hit={transformHit(hit, selectedPriceDayOffset, selectedStoreId, selectedRegion) as Hit}
                  displayPrice={displayPrice}
                  navigate={navigate}
                  ATBEventDetails={{
                      locationOfCart: CART_LOCATION.AUTOCOMPLETE,
                      viewingCategoryId: '',
                      searchQuery: searchQuery
                    }}
                  onClick={(e) => {
                    addToBasket(e)
                    sendAddToCartData(
                      hit,
                      CART_LOCATION.AUTOCOMPLETE,
                      '', // No viewingCategoryId for autocomplete hits
                      searchQuery,
                      hitItemIndex
                    )
                  }}
                  paddingY="2px"
                  width="144px"
                  isSearch={true}
                />
                ) : undefined
              }
              style={{width: '100%', height: vars.space['32px']}}
            />
          </Box>
        </Flex>
      </Flex>
    )
  }
)
